import createTabStore from '../../store/createTabStore';
import SchemeDefinitionService from '../../services/schemedefination.service';

export const [useSchemeStoreAdd, useSchemeStoreAddApi] = createTabStore((set, get) => {
// Each tab owns its initial values as well as its actions.
const initialState = {

    // schemeCommon
    loading: false,
    error: null,
    serviceInstance: null,
    schemeId: null,
    tabValue: 0,
    prevTabValue: 0,
    formData: {},
    errorTab: null,
    validationErrorMessage: null,
    apiResponse: null,
    definitionData: null,
    liftingUOMName: null,


    // SchemeDefinition Tab
    schemeDetails: null,
    parentSchemeDetails: null,
    schemeControls: [],
    primaryCategoryControls: [],
    executiveDefControls: [],
    schemeDivisionControls: [],
    criteriaHandlingControls: [],
    newCustomerControls: [],
    distributorBeneficiaryControls: [],
    schemeData: null,
    schemeDocuments: [],
    conversionRateControl: [],
    pointBasedSlabRepeatationCriteriaControls:[],
    thresholdSettings: null,

};

    // Immediately fetch and store the service instance
    (async () => {
        const service = await SchemeDefinitionService;
        set({ serviceInstance: service });
    })();

    return {
        ...initialState,

        // Reset function
        reset: () => set(initialState),

        // Setters
        setDefinitionData: (data) => set({ definitionData: data }),
        setLiftingUOMName: (data) => set({ liftingUOMName: data }),
        setApiResponse: (data) => set({ apiResponse: data }),
        setSchemeId: (data) => set({ schemeId: data }),
        setFormData: (data) => set({ formData: data }),
        setTabValue: (data) => set({ tabValue: data }),
        setSchemeData: (data) => set({ schemeData: data }),
        setThresholdSettings: (data) => set({ thresholdSettings: data }),
        resetSchemeDetails: () => set({ schemeDetails: null }),


        addScheme: async () => { // NOSONAR
            set({ loading: true, error: null });
            try {
                const {                    
                    setApiResponse,
                    liftingUOMName,
                    setSchemeId,
                    schemeData,
                    schemeControls,
                    definitionData
                } = get();

                const {
                    SchemeDocument,
                    From_FromDate,
                    To_ToDate,
                    ...filteredData
                } = schemeData || {};

                const scheme = {
                    Id: '',
                    ...filteredData,
                    IsActive: true,
                    Filters: [],
                    Slabs : []
                };  

                // Handle SchemeDocument properly
                if (SchemeDocument && Array.isArray(SchemeDocument)) {
                    scheme.SchemeDocument = SchemeDocument.map(item => ({ Name: item?.name }));
                }

                const excludedKeys = ['From_FromDate', 'To_ToDate','SchemeDocument'];

                schemeControls.forEach(control => {
                    const key = control.ValueExpr;
                    if (!key || excludedKeys.includes(key)) return;

                    const isBooleanField = control.DataType === 'CheckBox';

                    if (isBooleanField) {
                        if (key === "IsActive") {
                            scheme[key] = schemeData?.[key] ?? true;  // keep IsActive true by default
                        } else {
                            scheme[key] = schemeData?.[key] ?? false;
                        }
                    } else {
                        const val = schemeData?.[key];
                        scheme[key] = (val !== undefined && val !== null && val !== "") ? val : null;
                    }
                });

                const payload = [
                    {
                        FromDate: From_FromDate,
                        ToDate: To_ToDate,
                        LiftingUOM: liftingUOMName || 'Target%',
                        Scheme: scheme
                    }
                ];

                //  Helper to clean nulls
                const replaceNullsWithEmptyStrings = (obj) => {
                    if (Array.isArray(obj)) {
                        return obj.map(item => replaceNullsWithEmptyStrings(item));
                    }
                    if (obj && typeof obj === 'object') {
                        return Object.fromEntries(
                            Object.entries(obj).map(([key, value]) => [key, replaceNullsWithEmptyStrings(value)])
                        );
                    } 
                        return obj === null ? "" : obj;
                    
                };

                const cleanPayload = replaceNullsWithEmptyStrings(payload);

                if (scheme?.IsApplySchemeConversion) {
                    cleanPayload[0].Scheme.SchemeConversionRateId = definitionData.SchemeConversionRateId;
                }

                const service = await SchemeDefinitionService;
                const formData = new FormData();

                formData.append("schemeJSON", JSON.stringify(cleanPayload));

                if (SchemeDocument && Array.isArray(SchemeDocument)) {
                    SchemeDocument.forEach((file) => {
                        if (file) formData.append('files', file);
                    });
                }               

                const response = await service.AddScheme(formData);
                setApiResponse(response || null);
                if (response?.data?.SchemeId) {
                    setSchemeId(response?.data?.SchemeId);
                }
                set({ loading: false });
            } catch (err) {
                console.error('Add scheme error:', err);
                set({ error: err, loading: false });
                throw err;
            }
        },

        fetchConversionRateControl: async (payload) => {
            set({ error: null });

            try {
                const {
                    schemeData
                } = get();
                const service = await SchemeDefinitionService;             

                const payloadd = {
                    "FromDate": schemeData?.From_FromDate,
                    "ToDate": schemeData?.To_ToDate,
                }

                if (Array.isArray(payloadd) && payloadd.length === 0) {
                    // If payload is an empty array, set empty array without calling API
                    set({
                        conversionRateControl: [],
                    });
                } else {
                    // Otherwise, treat it as a valid payload (assumed to be an object)
                    set({ loading: true });
                    const response = await service.GetSchemeConversionRateList(payloadd);
                    set({
                        conversionRateControl: response.data,
                        loading: false,
                    });
                }
            } catch (err) {
                console.error('Controls fetch error:', err);
                set({ error: 'Failed to fetch scheme controls.', loading: false });
            }
        },

        fetchParentSchemeDetails: async (parentId) => {
            set({ loading: true, error: null });
            try {
                const service = await SchemeDefinitionService;
                const response = await service.GetSchemeDetails(parentId);

                set({
                    parentSchemeDetails: response.data,
                    loading: false,
                });
            } catch (err) {
                console.error('Parent scheme fetch error:', err);
                set({ error: 'Failed to fetch parent scheme details.', loading: false });
            }
        },

        fetchThresholdSettings: async () => {
            set({ loading: true, error: null });
            try {
                const service = await SchemeDefinitionService;
                const response = await service.GetThresholdSetting();
                set({
                    thresholdSettings: response.data,
                    loading: false,
                });
            } catch (err) {
                console.error('Error fetching threshold settings:', err);
                set({ error: 'Failed to fetch threshold settings.', loading: false });
            }
        },

        fetchSchemeControls: async () => {
            set({ loading: true, error: null });
            try {
                const service = await SchemeDefinitionService;
                const [
                    controlsRes,
                    primaryCalcControlsRes,
                    executiveDefControlsRes,
                    schemeDivisionControlsRes,
                    criteriaHandlingControlsRes,
                    newCustomerControlsRes,
                    distributorBeneficiaryControlsRes,
                    pointBasedSlabRepeatationCriteriaControlsRes,
                ] = await Promise.all([
                    service.GetSchemeMandatoryData(),
                    service.GetPrimaryCalculationCategoryControls(),
                    service.GetExecutiveDefinitionControls(),
                    service.GetIsSchemeDivisionWiseControls(),
                    service.GetSchemeCriteriaHandlingControls(),
                    service.GetNewCustomerDefinitionControls(),
                    service.GetDistributorBeneficiaryControls(),
                    service.GetSecondarySchemesSlabRepeatationCriteriaControls(),
                ]);

                set({
                    schemeControls: controlsRes.data,
                    primaryCategoryControls: primaryCalcControlsRes.data,
                    executiveDefControls: executiveDefControlsRes.data,
                    schemeDivisionControls: schemeDivisionControlsRes.data,
                    criteriaHandlingControls: criteriaHandlingControlsRes.data,
                    newCustomerControls: newCustomerControlsRes.data,
                    distributorBeneficiaryControls: distributorBeneficiaryControlsRes.data,
                    pointBasedSlabRepeatationCriteriaControls: pointBasedSlabRepeatationCriteriaControlsRes.data,
                    loading: false,
                });
            } catch (err) {
                console.error('Scheme fetch error:', err);
                set({ error: 'Failed to fetch scheme controls.', loading: false });
            }
        },
    };
});
